/**
 * Asset manifest — preloads all assets required for offline gameplay.
 */

import { appConfig } from '@/app/config/appConfig';
import { getCloudFrontAssetUrl } from '@/utils/asset-utils';
import stage1RunningIcon from '@micro-screens/assets/icons/stage-1/running.png';
import stage1StarIcon from '@micro-screens/assets/icons/stage-1/star.png';
import stage2RunningIcon from '@micro-screens/assets/icons/stage-2/running.png';
import stage2StarIcon from '@micro-screens/assets/icons/stage-2/star.png';
import stage3RunningIcon from '@micro-screens/assets/icons/stage-3/running.png';
import stage3StarIcon from '@micro-screens/assets/icons/stage-3/star.png';
import stage4RunningIcon from '@micro-screens/assets/icons/stage-4/running.png';
import stage4StarIcon from '@micro-screens/assets/icons/stage-4/star.png';
import stage5RunningIcon from '@micro-screens/assets/icons/stage-5/running.png';
import stage5StarIcon from '@micro-screens/assets/icons/stage-5/star.png';
import stage6RunningIcon from '@micro-screens/assets/icons/stage-6/running.png';
import stage6StarIcon from '@micro-screens/assets/icons/stage-6/star.png';
import stage7RunningIcon from '@micro-screens/assets/icons/stage-7/running.png';
import stage7StarIcon from '@micro-screens/assets/icons/stage-7/star.png';
import mandalaVectorImage from '@micro-screens/assets/mandala-vector.png';
import { INSTRUCTION_ICONS } from '@micro-screens/src/section/instructions/instruction-icons';

import { type BoardJson } from '../features/game/engine/board-pool';

// ── Public assets served from the app origin ─────────────────────────────

const PUBLIC_ASSET_URLS: string[] = [
  '/sounds/background_sound.mp3',
  '/sounds/tap_sound.mp3',
  '/sounds/whoosh_sound.mp3',
  '/sounds/wrong_step.mp3',
  '/sounds/success_sound.mp3',
];

// ── CDN overlay images used by micro-screens ─────────────────────────────

const SECONDARY_OVERLAY_FILE_BY_STAGE: Record<number, string> = {
  1: 'end_overlay.png',
  2: 'end_overlay.png',
  3: 'end_overlay.png',
  4: 'end_overlay2.png',
  5: 'end_overlay5.png',
  6: 'end_overlay6.png',
  7: 'end_overlay7.png',
};

const bootStage = appConfig.game.stageNo;

const CDN_OVERLAY_URLS = [
  getCloudFrontAssetUrl(`stage-${String(bootStage)}/Stage_${String(bootStage)}.png`),
  getCloudFrontAssetUrl(`stage-${String(bootStage)}/stage-${String(bootStage)}-ending.png`),
  getCloudFrontAssetUrl(SECONDARY_OVERLAY_FILE_BY_STAGE[bootStage] ?? 'end_overlay.png'),
];

// ── Bundled stage icons (Vite-resolved import paths) ─────────────────────

const BUNDLED_ICON_URLS = [
  ...Object.values(INSTRUCTION_ICONS),
  stage1RunningIcon,
  stage1StarIcon,
  stage2RunningIcon,
  stage2StarIcon,
  stage3RunningIcon,
  stage3StarIcon,
  stage4RunningIcon,
  stage4StarIcon,
  stage5RunningIcon,
  stage5StarIcon,
  stage6RunningIcon,
  stage6StarIcon,
  stage7RunningIcon,
  stage7StarIcon,
  mandalaVectorImage,
];

// ── Glob all board JSONs to extract their image URLs ─────────────────────

type BoardModule = { default: BoardJson };

const easyModules = import.meta.glob<BoardModule>(
  '../features/game/data/boards/easy/*.json',
  { eager: true }
);
const mediumModules = import.meta.glob<BoardModule>(
  '../features/game/data/boards/medium/*.json',
  { eager: true }
);
const hardModules = import.meta.glob<BoardModule>(
  '../features/game/data/boards/hard/*.json',
  { eager: true }
);
const demoModules = import.meta.glob<BoardModule>(
  '../features/game/data/boards/demo/*.json',
  { eager: true }
);

function extractUrls(modules: Record<string, BoardModule>): string[] {
  return Object.values(modules).map((mod) => mod.default.fileUrl);
}

const BOARD_IMAGE_URLS = [
  ...extractUrls(easyModules),
  ...extractUrls(mediumModules),
  ...extractUrls(hardModules),
  ...extractUrls(demoModules),
];

// ── Object URL cache ─────────────────────────────────────────────────────

const objectUrlCache = new Map<string, string>();

export function getPreloadedAssetUrl(originalUrl: string): string {
  return objectUrlCache.get(originalUrl) ?? originalUrl;
}

// ── Fetchers ─────────────────────────────────────────────────────────────

async function fetchAsset(url: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to preload asset: ${url} (${String(response.status)})`);
  }
  const blob = await response.blob();
  objectUrlCache.set(url, URL.createObjectURL(blob));
}

// Keep a reference to prevent garbage collection and force memory-caching
const imageCache: HTMLImageElement[] = [];

function preloadImage(url: string, strict = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      imageCache.push(img);
      resolve();
    };
    img.onerror = () => {
      if (strict) {
        reject(new Error(`Failed to preload image: ${url}`));
      } else {
        console.warn(`Failed to preload image (non-strict): ${url}`);
        resolve();
      }
    };
    img.src = url;
  });
}

// ── Main preloader ───────────────────────────────────────────────────────

export async function preloadAllAssets(): Promise<void> {
  await Promise.all([
    ...PUBLIC_ASSET_URLS.map(fetchAsset),
    ...BUNDLED_ICON_URLS.map((url) => preloadImage(url, false)),
    ...CDN_OVERLAY_URLS.map((url) => preloadImage(url, false)),
    ...BOARD_IMAGE_URLS.map((url) => preloadImage(url, true)),
  ]);
}
